import { useSession } from "hooks/api/useSession";
import { Box, Stack, Text, Collapsible, Button } from "prism-react";
import React from "react";
import { ErrorBoundary as DefaultErrorBoundary } from "react-error-boundary";

import { useAnalytics } from "hooks/useAnalytics";

import type { KnownErrors } from "./GlobalError";
import { ErrorDetails } from "./common";
import { useGlobalErrorMatcher } from "./useGlobalErrorMatcher";

export const ErrorBoundary: React.FC<{
  context: string;
  children: React.ReactNode;
}> = ({ children, context }) => {
  return (
    <DefaultErrorBoundary
      fallbackRender={({ error }) => {
        function parseErrorMessage(errorMessage: string) {
          try {
            return JSON.parse(errorMessage).message || errorMessage;
          } catch {
            return errorMessage;
          }
        }

        return (
          <LocalError
            context={{
              context,
              error: error?.message
                ? parseErrorMessage(error.message)
                : "RUNTIME_ERROR",
            }}
            errorCode="RUNTIME_ERROR"
          />
        );
      }}
    >
      {children}
    </DefaultErrorBoundary>
  );
};

const LocalError: React.FC<{
  children?: never;

  /** Provide a knwon error code, which will render a proper cause and solution */
  errorCode: KnownErrors;

  context: {
    /** Original error  */
    error: string;
    /** Provide any other context about this error */
    context?: string;
    /** What state was the application in when this error occured? */
    state?: string;
    /** Did this error occur when handling a specific event? */
    event?: string;
    /** In some instances, branchId is useful for debugging container problems */
    branchId?: string;
  };
}> = ({ context, errorCode }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const track = useAnalytics();

  const [session] = useSession();
  const cause = useGlobalErrorMatcher(
    errorCode,
    context.error,
    session.state === "AUTHENTICATED",
  );

  React.useEffect(() => {
    track("error_page", {
      description: errorCode ?? "UNKNOWN_ERROR",
      error: context.error,
      event: context.event,
      context: context.context,
      event_source: "editor",
    });
  }, []);

  return (
    <Box css={{ lineHeight: "140%" }} selectable>
      <Box
        css={{
          height: "100%",
          padding: "$4",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Stack
          css={{ "@media screen and (min-width: 1024px)": { maxWidth: 528 } }}
          gap={6}
          selectable
        >
          <Text as="h1" color="neutral-fg-high" size="lg" weight="semibold">
            {cause.title}
          </Text>
          <Text as="div">{cause.description}</Text>

          <Text as="p">
            If the issue persists,{" "}
            <strong>please contact our support team at</strong>{" "}
            <Text
              as="a"
              color="neutral-fg-high"
              href="mailto:support@codesandbox.io"
            >
              support@codesandbox.io
            </Text>{" "}
            for further assistance, making sure to copy the error details below.
          </Text>

          <Collapsible onOpenChange={setIsOpen} open={isOpen}>
            <Collapsible.Trigger open={isOpen}>
              <Text>Problem details and configurations</Text>
            </Collapsible.Trigger>
            <Collapsible.Content>
              <ErrorDetails
                error={context.error}
                versions={[
                  ["Context", context.context],
                  ["Event", context.event],
                  ["State", context.state],
                  ["Branch", context.branchId],
                ]}
              />
            </Collapsible.Content>
          </Collapsible>

          <Stack css={{ flexShrink: 0 }} gap={3} horizontal>
            {cause.recoverButton}

            <Button onClick={() => setIsOpen((p) => !p)} variant="secondary">
              {isOpen ? "Hide details" : "Show details"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
